import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useCreateChat } from '../hooks/useMessages';
import PropertyImage from '../components/PropertyImage';
import LiveMap from '../components/LiveMap';
import ViewingRequestModal from '../components/ViewingRequestModal';
import propertyAnalytics from '../services/propertyAnalytics.service';
import { resolveMessagingLandlordUid } from '../utils/firestoreQueries';
import { 
  MapPin, 
  Euro, 
  Bed, 
  Calendar,
  MessageCircle,
  Heart,
  Share2,
  ArrowLeft,
  Star,
  User,
  Eye,
  Home,
  Car,
  Wifi,
  Utensils,
  Bath,
  Square,
  Clock,
  Shield,
  CheckCircle,
  X,
  Navigation,
  ExternalLink,
  Play,
  Camera,
  ChevronLeft,
  ChevronRight,
  Droplet,
  Zap,
  Flame,
  Tv,
  Users,
  Building2,
  FileText,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Battery,
  Wind,
  Waves,
  DoorOpen,
  Lock,
  FileCheck,
  Calculator,
  Info,
  Thermometer,
  Radio,
  Refrigerator,
  ChefHat,
  Microwave,
  Sparkles,
  TreePine,
  Flower2,
  Umbrella,
  FlameIcon,
  AlertTriangle,
  Ban,
  Check,
  Minus
} from 'lucide-react';
import toast from 'react-hot-toast';

// Helper function to calculate total monthly cost
const calculateTotalMonthlyCost = (property) => {
  if (!property) return 0;
  
  const baseRent = property.prijs || 0;
  const water = property.waterInbegrepen ? 0 : (property.waterKosten || 0);
  const electricity = property.elektriciteitInbegrepen ? 0 : (property.elektriciteitKosten || 0);
  const heating = property.verwarmingInbegrepen ? 0 : (property.verwarmingKosten || 0);
  const internet = property.internetInbegrepen ? 0 : (property.internetKosten || 0);
  const tv = property.tvInbegrepen ? 0 : (property.tvKosten || 0);
  const cleaning = property.poetsdienstKosten || 0;
  const maintenance = property.onderhoudKosten || 0;
  const common = property.gemeenschappelijkeKosten || 0;
  
  return baseRent + water + electricity + heating + internet + tv + cleaning + maintenance + common;
};

// Helper component for Yes/No display
const YesNoDisplay = ({ value }) => (
  <span className={`font-medium ${value ? 'text-green-600' : 'text-gray-500'}`}>
    {value ? 'Ja' : 'Nee'}
  </span>
);

// Helper component for Info Row
const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
    <div className="flex items-center space-x-2 text-gray-600">
      {Icon && <Icon className="w-4 h-4" />}
      <span>{label}</span>
    </div>
    <span className="font-medium text-gray-900">{value}</span>
  </div>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const { user, landlordType } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const previewProperty = location.state?.previewProperty;
  const previewMeta = location.state?.previewMeta;
  const isPreview = Boolean(previewProperty);
  const backTarget = isPreview
    ? (landlordType === 'agency' ? '/landlord/agency/crm' : '/landlord/crm')
    : '/';
  const [property, setProperty] = useState(null);
  const [contactProfile, setContactProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showViewingModal, setShowViewingModal] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const { createChat, creating } = useCreateChat();
  const canScheduleViewings = true;
  const hasViewingSlots = Array.isArray(property?.viewingSlots) && property.viewingSlots.length > 0;
  const canTenantPlanViewing = !isPreview && property?.viewingsEnabled && (canScheduleViewings || hasViewingSlots);
  const energyLabelTitle = (property?.countryCode === 'NL' || property?.land === 'Nederland') ? 'Energielabel' : 'EPC';

  useEffect(() => {
    if (previewProperty) {
      setProperty(previewProperty);
      setLoading(false);
      return;
    }
    if (id) {
      loadProperty();
      propertyAnalytics.trackPropertyView(id);
    }
  }, [id, previewProperty]);

  // Reset image index when property changes
  useEffect(() => {
    if (property) {
      setCurrentImageIndex(0);
    }
  }, [property?.id]);

  const loadProperty = async () => {
    try {
      setLoading(true);
      
      // Load property
      const propertyDoc = await getDoc(doc(db, 'koten', id));
      if (!propertyDoc.exists()) {
        toast.error('Property not found');
        return;
      }
      
      const propertyData = propertyDoc.data();
      setProperty({ id: propertyDoc.id, ...propertyData });
      
      // Load contact profile for the same UID we use in chat participants (ownerUid before legacy ids).
      const contactUserId = resolveMessagingLandlordUid({
        id: propertyDoc.id,
        ...propertyData
      });

      if (contactUserId) {
        const contactDoc = await getDoc(doc(db, 'users', contactUserId));
        if (contactDoc.exists()) {
          setContactProfile(contactDoc.data());
        } else {
          setContactProfile(null);
        }
      } else {
        setContactProfile(null);
      }
      
      // Check if favorite
      if (user) {
        // TODO: Check if property is in user's favorites
      }
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Failed to load property');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      navigate(`/user-type-login?redirect=${encodeURIComponent(`/property/${id}`)}`);
      toast('Log in om favorieten op te slaan', { icon: '🔒' });
      return;
    }
    
    try {
      // TODO: Implement favorite functionality
      setIsFavorite(!isFavorite);
      toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const contactLandlord = async () => {
    if (isPreview) {
      toast('Preview only');
      return;
    }
    console.log('=== CONTACT LANDLORD ===');
    console.log('User:', user?.uid);
    console.log('Property:', property);
    
    if (!user) {
      navigate(`/user-type-login?redirect=${encodeURIComponent(`/property/${id}`)}`);
      toast('Log in om contact op te nemen', { icon: '🔒' });
      return;
    }
    
    if (!property) {
      console.log('❌ No property data');
      toast.error('Property informatie niet beschikbaar');
      return;
    }
    
    const landlordId = resolveMessagingLandlordUid(property);
    if (!landlordId) {
      console.log('❌ No landlord ID');
      toast.error('Verhuurder informatie niet beschikbaar');
      return;
    }

    try {
      console.log('=== CONTACT LANDLORD ===');
      console.log('Creating chat between:', user.uid, 'and', landlordId);
      console.log('Property ID:', property.id);
      
      const chat = await createChat(user.uid, landlordId, property.id);
      console.log('=== CHAT RESULT ===');
      console.log('Chat object:', chat);
      console.log('Chat type:', typeof chat);
      console.log('Chat keys:', chat ? Object.keys(chat) : 'null');
      console.log('Chat ID:', chat?.id);
      
      if (!chat) {
        console.log('❌ Chat creation returned null/undefined');
        toast.error('Chat kon niet worden aangemaakt - geen resultaat');
        return;
      }
      
      // Extract chat ID - handle different return types
      let extractedChatId = null;
      
      if (typeof chat === 'string') {
        extractedChatId = chat;
      } else if (chat && chat.id) {
        extractedChatId = chat.id;
      } else if (chat && chat.chatId) {
        extractedChatId = chat.chatId;
      } else if (chat && chat._key) {
        extractedChatId = chat._key.path?.segments?.pop();
      }
      
      console.log('Extracted chat ID:', extractedChatId);
      console.log('Chat object keys:', chat ? Object.keys(chat) : 'null');
      
      if (extractedChatId && extractedChatId.trim() !== '') {
        console.log('✅ Chat created successfully with ID:', extractedChatId);
        console.log('Navigating to:', `/messages?threadId=${extractedChatId}`);
        
        // Small delay to ensure chat is saved in Firestore
        setTimeout(() => {
          navigate(`/messages?threadId=${extractedChatId}`, { replace: false });
        }, 200);
        
        toast.success('Chat gestart!');
      } else {
        console.log('❌ Chat creation failed - no chat ID found');
        console.log('Full chat object:', chat);
        console.log('Chat object type:', typeof chat);
        console.log('Chat object stringified:', JSON.stringify(chat, null, 2));
        toast.error('Chat kon niet worden aangemaakt - geen chat ID. Check console voor details.');
      }
    } catch (error) {
      console.error('❌ Error creating chat:', error);
      console.error('Error stack:', error.stack);
      toast.error('Chat kon niet gestart worden. Probeer opnieuw.');
    }
  };

  const scheduleViewing = () => {
    if (!user) {
      navigate(`/user-type-login?redirect=${encodeURIComponent(`/property/${id}`)}`);
      toast('Log in om een bezichtiging te plannen', { icon: '🔒' });
      return;
    }
    setShowViewingModal(true);
  };

  const shareProperty = async () => {
    const shareUrl = `${window.location.origin}/p/${id}`;
    const shareTitle = property?.titel || 'Bekijk dit pand op StudyStay';
    const shareText = property?.stad
      ? `${shareTitle} in ${property.stad} - €${property.prijs || 0}/maand`
      : shareTitle;

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      } catch (error) {
        if (error.name !== 'AbortError') console.log('Share error:', error);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link gekopieerd!');
      } catch {
        toast.error('Kopiëren mislukt');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Property not found</h1>
          <Link to="/properties" className="btn-primary">
            Back to Properties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
      {/* Modern Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to={backTarget}
                className="inline-flex items-center space-x-2 text-gray-700 hover:text-gray-900 p-2 hover:bg-gray-100/80 rounded-xl transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Terug</span>
              </Link>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {property?.titel || 'Pand Details'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleFavorite}
                className={`p-3 rounded-xl transition-all duration-200 hover:scale-105 ${
                  isFavorite
                    ? 'bg-red-50 text-red-600 shadow-md'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
              <button
                onClick={shareProperty}
                className="p-3 bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-200 hover:scale-105"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {isPreview && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-900">
            <div className="font-semibold mb-2">Preview modus (CRM)</div>
            {(previewMeta?.missingFields || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {previewMeta.missingFields.map((field) => (
                  <span
                    key={field}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-red-50 text-red-700"
                  >
                    {field}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Enhanced Image Gallery */}
            <div className="mb-8">
              {(() => {
                // Get images from multiple possible field names
                const imageUrls = property?.images || property?.afbeeldingURLs || property?.fotos || property?.imageUrls || [];
                const hasImages = Array.isArray(imageUrls) && imageUrls.length > 0 && imageUrls.filter(url => url && url.trim() !== '').length > 0;
                const validImageUrls = hasImages ? imageUrls.filter(url => url && url.trim() !== '') : [];
                
                if (hasImages && validImageUrls.length > 0) {
                  return (
                    <div className="relative group">
                      <div 
                        className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl cursor-pointer"
                        onClick={() => setShowFullscreenImage(true)}
                      >
                        <img
                          src={validImageUrls[currentImageIndex]}
                          alt={`${property?.titel || 'Property'} - Image ${currentImageIndex + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => {
                            console.error('Image failed to load:', validImageUrls[currentImageIndex]);
                            e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                          }}
                        />
                        
                        {/* Overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        
                        {/* Click to expand hint */}
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-full text-sm font-medium text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <Camera className="w-4 h-4 inline mr-1" />
                          Klik om te vergroten
                        </div>
                      </div>
                      
                      {validImageUrls.length > 1 && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(Math.max(0, currentImageIndex - 1));
                            }}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-3 shadow-xl transition-all duration-200 hover:scale-110 z-10"
                          >
                            <ChevronLeft className="w-6 h-6 text-gray-700" />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndex(Math.min(validImageUrls.length - 1, currentImageIndex + 1));
                            }}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm hover:bg-white rounded-full p-3 shadow-xl transition-all duration-200 hover:scale-110 z-10"
                          >
                            <ChevronRight className="w-6 h-6 text-gray-700" />
                          </button>
                        </>
                      )}
                      
                      {/* Image Counter */}
                      {validImageUrls.length > 1 && (
                        <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-xl">
                          {currentImageIndex + 1} / {validImageUrls.length}
                        </div>
                      )}
                      
                      {/* Image Thumbnails */}
                      {validImageUrls.length > 1 && (
                        <div className="flex space-x-3 mt-6 overflow-x-auto pb-2 scrollbar-hide" data-tutorial="property-images">
                          {validImageUrls.map((imageUrl, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden transition-all duration-200 ${
                                index === currentImageIndex 
                                  ? 'ring-4 ring-blue-500 shadow-lg scale-105' 
                                  : 'ring-2 ring-gray-200 hover:ring-gray-300 hover:scale-102'
                              }`}
                            >
                              <img
                                src={imageUrl}
                                alt={`Thumbnail ${index + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/100?text=Error';
                                }}
                              />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                } else {
                  return (
                    <div className="w-full h-[500px] bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center shadow-xl">
                      <div className="text-gray-400 text-center">
                        <div className="w-20 h-20 bg-white/50 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                          <Camera className="w-10 h-10" />
                        </div>
                        <p className="text-lg font-medium">Geen foto's beschikbaar</p>
                        <p className="text-sm mt-1">Foto's worden binnenkort toegevoegd</p>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
            
            {/* Fullscreen Image Modal */}
            {showFullscreenImage && (() => {
              const imageUrls = property?.images || property?.afbeeldingURLs || property?.fotos || property?.imageUrls || [];
              const validImageUrls = imageUrls.filter(url => url && url.trim() !== '');
              if (validImageUrls.length === 0) return null;
              
              return (
                <div 
                  className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4"
                  onClick={() => setShowFullscreenImage(false)}
                >
                  <button
                    onClick={() => setShowFullscreenImage(false)}
                    className="absolute top-4 right-4 text-white hover:text-gray-300 p-3 bg-white/10 rounded-full backdrop-blur-sm transition-all duration-200 hover:scale-110"
                  >
                    <X className="w-6 h-6" />
                  </button>
                  
                  {validImageUrls.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(Math.max(0, currentImageIndex - 1));
                        }}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-4 text-white transition-all duration-200 hover:scale-110"
                      >
                        <ChevronLeft className="w-8 h-8" />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex(Math.min(validImageUrls.length - 1, currentImageIndex + 1));
                        }}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-4 text-white transition-all duration-200 hover:scale-110"
                      >
                        <ChevronRight className="w-8 h-8" />
                      </button>
                    </>
                  )}
                  
                  <img
                    src={validImageUrls[currentImageIndex]}
                    alt={`${property?.titel || 'Property'} - Fullscreen`}
                    className="max-w-full max-h-[90vh] object-contain rounded-lg"
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  {validImageUrls.length > 1 && (
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-6 py-3 rounded-full text-sm font-medium">
                      {currentImageIndex + 1} / {validImageUrls.length}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Property Information - Enhanced with all sections */}
            <div className="space-y-6">
              {/* Header with Title and Quick Info */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                  {property?.titel || 'Naamloos pand'}
                </h1>
                <div className="flex items-center text-gray-600 mb-6">
                  <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                  <span className="text-lg font-medium">
                    {property?.adres}, {property?.stad}{property?.postcode && ` ${property.postcode}`}
                    {property?.land && `, ${property.land}`}
                  </span>
                </div>
                
                <div className="flex flex-wrap items-center gap-6 text-lg">
                  <div className="flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 rounded-xl border border-blue-200">
                    <Euro className="w-6 h-6 mr-2 text-blue-600" />
                    <span className="text-blue-700 font-bold text-xl">€{property?.prijs?.toLocaleString('nl-NL') || 0}</span>
                    <span className="text-blue-600 ml-1">/maand</span>
                  </div>
                  
                  <div className="flex items-center bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                    <Square className="w-5 h-5 mr-2 text-gray-600" />
                    <span className="text-gray-700 font-semibold">{property?.oppervlakte || 0} m²</span>
                  </div>
                  
                  <div className="flex items-center bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                    <Bed className="w-5 h-5 mr-2 text-gray-600" />
                    <span className="text-gray-700 font-semibold">
                      {property?.slaapkamers || property?.kamers || 0} {(property?.slaapkamers || property?.kamers) === 1 ? 'slaapkamer' : 'slaapkamers'}
                    </span>
                  </div>
                  
                  {property?.badkamers > 0 && (
                    <div className="flex items-center bg-gray-50 px-4 py-3 rounded-xl border border-gray-200">
                      <Bath className="w-5 h-5 mr-2 text-gray-600" />
                      <span className="text-gray-700 font-semibold">
                        {property.badkamers} {property.badkamers === 1 ? 'badkamer' : 'badkamers'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 💬 Beschrijving — shown above basic info */}
              {(property?.beschrijving || property?.omschrijving) && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <FileText className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">💬 Beschrijving</h2>
                      <p className="text-sm text-gray-500 mt-1">Meer informatie over het pand</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line text-base">{property.beschrijving || property.omschrijving}</p>
                </div>
              )}

              {/* 🏡 1️⃣ Basisinformatie */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Home className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">🏡 Basisinformatie</h2>
                    <p className="text-sm text-gray-500 mt-1">Algemene informatie over het pand</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <InfoRow label="Type woning" value={property?.type || 'Niet opgegeven'} icon={Building2} />
                    <InfoRow label="Oppervlakte" value={`${property?.oppervlakte || 0} m²`} icon={Square} />
                    <InfoRow label="Aantal kamers" value={property?.slaapkamers || property?.kamers || 0} icon={Bed} />
                    <InfoRow label="Aantal badkamers" value={property?.badkamers || 0} icon={Bath} />
                  </div>
                  <div className="space-y-3">
                    <InfoRow label="Max. bewoners" value={property?.maxBewoners || 1} icon={Users} />
                    {property?.energyLabel && (
                      <InfoRow label={energyLabelTitle} value={property.energyLabel} icon={Home} />
                    )}
                    <InfoRow 
                      label="Beschikbaar vanaf" 
                      value={property?.beschikbaarVanaf || property?.moveInDate 
                        ? new Date(property.beschikbaarVanaf?.toDate?.() || property.moveInDate?.toDate?.() || property.beschikbaarVanaf || property.moveInDate).toLocaleDateString('nl-NL')
                        : 'Niet opgegeven'} 
                      icon={Calendar} 
                    />
                    <InfoRow label="Min. huurperiode" value={`${property?.minimaleContractduur || 6} maanden`} icon={Clock} />
                    <InfoRow 
                      label="Beschikbaarheid" 
                      value={property?.beschikbaarheid === 'vrij' ? 'Vrij' : property?.beschikbaarheid === 'bezet' ? 'Bezet' : property?.beschikbaarheid === 'binnenkort_vrij' ? 'Binnenkort vrij' : (property?.beschikbaar ? 'Vrij' : 'Bezet')} 
                      icon={Info} 
                    />
                  </div>
                </div>
                
                {property?.adres && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="flex items-start space-x-2">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-gray-900">{property.adres}</p>
                        <p className="text-sm text-gray-600">
                          {property.postcode} {property.stad}
                          {property.land && `, ${property.land}`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 💰 2️⃣ Prijs & kosten */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <Euro className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">💰 Prijs & Kosten</h2>
                    <p className="text-sm text-gray-500 mt-1">Overzicht van alle kosten</p>
                  </div>
                </div>
                
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 shadow-md">
                      <div className="flex items-center justify-between">
                        <span className="text-base font-semibold text-gray-700">Huurprijs per maand</span>
                        <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                          €{(property?.prijs || 0).toLocaleString('nl-NL')}
                        </span>
                      </div>
                    </div>
                  
                  <div className="space-y-3">
                    <InfoRow 
                      label="Waarborg" 
                      value={property?.deposit || property?.waarborg ? `€${(property.deposit || property.waarborg).toLocaleString('nl-NL')}` : 'Niet opgegeven'} 
                      icon={Shield} 
                    />
                    {property?.waarborgVoorwaarden && (
                      <div className="text-sm text-gray-600 mt-2 pl-6">
                        <p className="font-medium mb-1">Voorwaarden:</p>
                        <p>{property.waarborgVoorwaarden}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="font-medium text-gray-900 mb-3">Nutsvoorzieningen & Vaste Kosten</h3>
                    <div className="space-y-2">
                      <InfoRow 
                        label="Water" 
                        value={property?.waterInbegrepen ? 'Inbegrepen' : (property?.waterKosten ? `€${property.waterKosten.toLocaleString('nl-NL')}/maand` : 'Niet opgegeven')} 
                        icon={Droplet} 
                      />
                      <InfoRow 
                        label="Elektriciteit" 
                        value={property?.elektriciteitInbegrepen ? 'Inbegrepen' : (property?.elektriciteitKosten ? `€${property.elektriciteitKosten.toLocaleString('nl-NL')}/maand` : 'Niet opgegeven')} 
                        icon={Zap} 
                      />
                      <InfoRow 
                        label="Verwarming" 
                        value={property?.verwarmingInbegrepen ? 'Inbegrepen' : (property?.verwarmingKosten ? `€${property.verwarmingKosten.toLocaleString('nl-NL')}/maand` : 'Niet opgegeven')} 
                        icon={Flame} 
                      />
                      <InfoRow 
                        label="Internet" 
                        value={property?.internetInbegrepen ? 'Inbegrepen' : (property?.internetKosten ? `€${property.internetKosten.toLocaleString('nl-NL')}/maand` : 'Niet opgegeven')} 
                        icon={Wifi} 
                      />
                      <InfoRow 
                        label="TV" 
                        value={property?.tvInbegrepen ? 'Inbegrepen' : (property?.tvKosten ? `€${property.tvKosten.toLocaleString('nl-NL')}/maand` : 'Niet opgegeven')} 
                        icon={Tv} 
                      />
                    </div>
                  </div>
                  
                  {(property?.poetsdienstKosten || property?.onderhoudKosten || property?.gemeenschappelijkeKosten) && (
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="font-medium text-gray-900 mb-3">Bijkomende Kosten</h3>
                      <div className="space-y-2">
                        {property.poetsdienstKosten > 0 && (
                          <InfoRow label="Poetsdienst" value={`€${property.poetsdienstKosten.toLocaleString('nl-NL')}/maand`} />
                        )}
                        {property.onderhoudKosten > 0 && (
                          <InfoRow label="Onderhoud" value={`€${property.onderhoudKosten.toLocaleString('nl-NL')}/maand`} />
                        )}
                        {property.gemeenschappelijkeKosten > 0 && (
                          <InfoRow label="Gemeenschappelijke kosten" value={`€${property.gemeenschappelijkeKosten.toLocaleString('nl-NL')}/maand`} />
                        )}
                      </div>
                    </div>
                  )}
                  
                  {(() => {
                    const totalMonthly = calculateTotalMonthlyCost(property);
                    const baseRent = property?.prijs || 0;
                    return totalMonthly > baseRent ? (
                      <div className="mt-6 pt-4 border-t-2 border-gray-300">
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                          <div className="flex items-center justify-between">
                            <span className="text-lg font-semibold text-gray-900">Totaalprijs per maand</span>
                            <span className="text-3xl font-bold text-green-600">€{totalMonthly.toLocaleString('nl-NL')}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">Inclusief alle kosten</p>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* ⚡ Nutsvoorzieningen & Faciliteiten */}
              {(property?.nutsvoorzieningen?.length > 0 || property?.andereNutsvoorzieningen || property?.wifiInbegrepen !== undefined || property?.wasmachine || property?.droogkast || property?.gemeenschappelijkeKeuken || property?.eigenBadkamer || property?.parkeerplaats || property?.fietsenstalling) && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                  <div className="flex items-center space-x-4 mb-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">⚡ Nutsvoorzieningen & Faciliteiten</h2>
                      <p className="text-sm text-gray-500 mt-1">Beschikbare voorzieningen</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {property?.nutsvoorzieningen?.length > 0 && (
                      <div>
                        <h3 className="text-md font-medium text-gray-900 mb-3">Nutsvoorzieningen</h3>
                        <div className="flex flex-wrap gap-2">
                          {property.nutsvoorzieningen.map((nut, index) => {
                            const nutLabels = {
                              'wifi': 'WiFi',
                              'wasmachine': 'Wasmachine',
                              'droogkast': 'Droogkast',
                              'gemeenschappelijkeKeuken': 'Gemeenschappelijke keuken',
                              'eigenBadkamer': 'Eigen badkamer',
                              'gedeeldSanitair': 'Gedeeld sanitair',
                              'gemeubileerd': 'Gemeubileerd',
                              'parkeerplaats': 'Parkeerplaats',
                              'fietsenstalling': 'Fietsenstalling',
                              'lift': 'Lift',
                              'balkon': 'Balkon',
                              'tuin': 'Tuin'
                            };
                            return (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                {nutLabels[nut] || nut}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {property?.andereNutsvoorzieningen && (
                      <div>
                        <h3 className="text-md font-medium text-gray-900 mb-2">Andere nutsvoorzieningen</h3>
                        <p className="text-gray-600 whitespace-pre-line">{property.andereNutsvoorzieningen}</p>
                      </div>
                    )}
                    
                    {property?.verwarmingstype && (
                      <div>
                        <InfoRow 
                          label="Verwarmingstype" 
                          value={property.verwarmingstype} 
                          icon={Flame} 
                        />
                      </div>
                    )}
                    
                    {property?.meubelsLijst && (
                      <div>
                        <h3 className="text-md font-medium text-gray-900 mb-2">Meubels</h3>
                        <p className="text-gray-600">{property.meubelsLijst}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 🪟 Interieur & Voorzieningen */}
              {(property?.verdieping !== undefined || property?.liftAanwezig !== undefined || property?.geluidsisolatie !== undefined || property?.dubbelGlas !== undefined || property?.airco !== undefined || property?.ventilatie !== undefined || property?.oven !== undefined || property?.koelkast !== undefined || property?.kookplaat !== undefined || property?.microgolf !== undefined || property?.vaatwasser !== undefined || property?.balkon !== undefined || property?.tuin !== undefined || property?.terras !== undefined) && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                  <div className="flex items-center space-x-4 mb-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Home className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">🪟 Interieur & Voorzieningen</h2>
                      <p className="text-sm text-gray-500 mt-1">Interieur details en extra voorzieningen</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property?.verdieping !== undefined && (
                      <InfoRow label="Verdieping" value={property.verdieping !== null ? property.verdieping.toString() : 'Niet opgegeven'} icon={Building2} />
                    )}
                    {property?.liftAanwezig !== undefined && (
                      <InfoRow label="Lift aanwezig" value={property.liftAanwezig ? 'Ja' : 'Nee'} icon={Building2} />
                    )}
                    {property?.geluidsisolatie !== undefined && (
                      <InfoRow label="Geluidsisolatie" value={property.geluidsisolatie ? 'Ja' : 'Nee'} icon={Shield} />
                    )}
                    {property?.dubbelGlas !== undefined && (
                      <InfoRow label="Dubbel glas" value={property.dubbelGlas ? 'Ja' : 'Nee'} icon={Shield} />
                    )}
                    {property?.airco !== undefined && (
                      <InfoRow label="Airco" value={property.airco ? 'Ja' : 'Nee'} icon={Wind} />
                    )}
                    {property?.ventilatie !== undefined && (
                      <InfoRow label="Ventilatie" value={property.ventilatie ? 'Ja' : 'Nee'} icon={Wind} />
                    )}
                    {property?.oven !== undefined && (
                      <InfoRow label="Oven" value={property.oven ? 'Ja' : 'Nee'} icon={ChefHat} />
                    )}
                    {property?.koelkast !== undefined && (
                      <InfoRow label="Koelkast" value={property.koelkast ? 'Ja' : 'Nee'} icon={Refrigerator} />
                    )}
                    {property?.kookplaat !== undefined && (
                      <InfoRow label="Kookplaat" value={property.kookplaat ? 'Ja' : 'Nee'} icon={ChefHat} />
                    )}
                    {property?.microgolf !== undefined && (
                      <InfoRow label="Magnetron" value={property.microgolf ? 'Ja' : 'Nee'} icon={Microwave} />
                    )}
                    {property?.vaatwasser !== undefined && (
                      <InfoRow label="Vaatwasser" value={property.vaatwasser ? 'Ja' : 'Nee'} icon={Waves} />
                    )}
                    {property?.balkon !== undefined && (
                      <InfoRow label="Balkon" value={property.balkon ? 'Ja' : 'Nee'} icon={Umbrella} />
                    )}
                    {property?.tuin !== undefined && (
                      <InfoRow label="Tuin" value={property.tuin ? 'Ja' : 'Nee'} icon={TreePine} />
                    )}
                    {property?.terras !== undefined && (
                      <InfoRow label="Terras" value={property.terras ? 'Ja' : 'Nee'} icon={Umbrella} />
                    )}
                  </div>
                </div>
              )}

              {/* 🚪 Veiligheid & Regels */}
              {(property?.branddetectie !== undefined || property?.nooduitgang !== undefined || property?.brandblusser !== undefined || property?.rookmelder !== undefined || property?.huisdierenToegestaan !== undefined || property?.bezoekToegestaan !== undefined || property?.rokersToegestaan !== undefined || property?.domicilieMogelijk !== undefined) && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                  <div className="flex items-center space-x-4 mb-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Shield className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">🚪 Veiligheid & Regels</h2>
                      <p className="text-sm text-gray-500 mt-1">Veiligheidsvoorzieningen en huisregels</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property?.branddetectie !== undefined && (
                      <InfoRow label="Branddetectie" value={property.branddetectie ? 'Ja' : 'Nee'} icon={AlertTriangle} />
                    )}
                    {property?.nooduitgang !== undefined && (
                      <InfoRow label="Nooduitgang" value={property.nooduitgang ? 'Ja' : 'Nee'} icon={DoorOpen} />
                    )}
                    {property?.brandblusser !== undefined && (
                      <InfoRow label="Brandblusser" value={property.brandblusser ? 'Ja' : 'Nee'} icon={Shield} />
                    )}
                    {property?.rookmelder !== undefined && (
                      <InfoRow label="Rookmelder" value={property.rookmelder ? 'Ja' : 'Nee'} icon={AlertCircle} />
                    )}
                    {property?.huisdierenToegestaan !== undefined && (
                      <InfoRow label="Huisdieren toegestaan" value={property.huisdierenToegestaan ? 'Ja' : 'Nee'} icon={property.huisdierenToegestaan ? CheckCircle : Ban} />
                    )}
                    {property?.bezoekToegestaan !== undefined && (
                      <InfoRow label="Bezoek toegestaan" value={property.bezoekToegestaan ? 'Ja' : 'Nee'} icon={property.bezoekToegestaan ? CheckCircle : Ban} />
                    )}
                    {property?.rokersToegestaan !== undefined && (
                      <InfoRow label="Roken toegestaan" value={property.rokersToegestaan ? 'Ja' : 'Nee'} icon={property.rokersToegestaan ? CheckCircle : Ban} />
                    )}
                    {property?.domicilieMogelijk !== undefined && (
                      <InfoRow label="Domicilie mogelijk" value={property.domicilieMogelijk ? 'Ja' : 'Nee'} icon={property.domicilieMogelijk ? CheckCircle : Ban} />
                    )}
                  </div>
                </div>
              )}

              {/* Location & Map */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-8">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">📍 Locatie</h2>
                    <p className="text-sm text-gray-500 mt-1">Adres en routebeschrijving</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <MapPin className="w-5 h-5" />
                    <span>{property?.adres}, {property?.stad}{property?.postcode && ` ${property.postcode}`}</span>
                  </div>
                  
                      {/* Live Map */}
                  {(property?.latitude && property?.longitude) || property?.adres ? (
                    <div className="space-y-4" data-tutorial="property-map">
                      <div className="bg-white rounded-xl p-4 border border-gray-200">
                        <LiveMap
                          latitude={property?.latitude}
                          longitude={property?.longitude}
                          address={property?.adres}
                          city={property?.stad}
                          className="h-96 w-full"
                        />
                      </div>
                      
                      {/* Map Actions */}
                      <div className="flex flex-wrap gap-4">
                        {property?.latitude && property?.longitude ? (
                          <>
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${property.latitude},${property.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <Navigation className="w-4 h-4" />
                              <span>Route plannen</span>
                            </a>
                            
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                              <span>Open in Google Maps</span>
                            </a>
                            
                            <a
                              href={`https://maps.apple.com/?q=${property.latitude},${property.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <MapPin className="w-4 h-4" />
                              <span>Open in Apple Maps</span>
                            </a>
                          </>
                        ) : (
                          <>
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(property.adres + (property.stad ? `, ${property.stad}` : ''))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                            >
                              <Navigation className="w-4 h-4" />
                              <span>Route plannen</span>
                            </a>
                            
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.adres + (property.stad ? `, ${property.stad}` : ''))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" />
                              <span>Open in Google Maps</span>
                            </a>
                            
                            <a
                              href={`https://maps.apple.com/?q=${encodeURIComponent(property.adres + (property.stad ? `, ${property.stad}` : ''))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                              <MapPin className="w-4 h-4" />
                              <span>Open in Apple Maps</span>
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Video Link */}
              {property?.videoLink && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Video</h2>
                  <a
                    href={property.videoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                  >
                    <Play className="w-5 h-5" />
                    <span>Bekijk video</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}

              {/* Contract Terms */}
              {property?.contractvoorwaarden && property.contractvoorwaarden.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Contractvoorwaarden</h2>
                  <div className="space-y-3">
                    {property.contractvoorwaarden.map((condition, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <Shield className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{condition}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Contact Info */}
              {contactProfile && (
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    {property?.management?.agencyId &&
                    ['active', 'requested'].includes(property?.management?.handoverStatus || 'active')
                      ? 'Immo'
                      : 'Verhuurder'}
                  </h3>
                  
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {contactProfile.displayName || contactProfile.companyName || contactProfile.name || 'Contact'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {property?.management?.agencyId &&
                        ['active', 'requested'].includes(property?.management?.handoverStatus || 'active')
                          ? 'Immo kantoor'
                          : 'Eigenaar'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center text-blue-600 text-sm">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    <span>Contacteer via in-app chat</span>
                    </div>
                </div>
              )}

              {/* Actions */}
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6">
                <div className="space-y-4">
                  <button
                    onClick={contactLandlord}
                    disabled={creating || isPreview}
                    className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
                    data-tutorial="contact-button"
                  >
                    {creating ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <MessageCircle className="w-5 h-5" />
                    )}
                    <span>
                      {creating
                        ? 'Chat starten...'
                        : property?.management?.agencyId &&
                          ['active', 'requested'].includes(property?.management?.handoverStatus || 'active')
                          ? 'Contact immo'
                          : 'Contact verhuurder'}
                    </span>
                  </button>
                  
                  {!isPreview && property?.viewingsEnabled && (
                    <button
                      onClick={() => {
                        if (!user) {
                          navigate(`/user-type-login?redirect=${encodeURIComponent(`/property/${id}`)}`);
                          toast('Log in om een bezichtiging te plannen', { icon: '🔒' });
                          return;
                        }
                        if (canTenantPlanViewing) {
                          setShowViewingModal(true);
                        } else {
                          toast.error('Bezichtigingsplanner tijdelijk niet beschikbaar. Probeer opnieuw.');
                        }
                      }}
                      className={`w-full py-4 px-6 rounded-xl font-semibold flex items-center justify-center space-x-2 transition-all duration-200 shadow-md ${
                        canTenantPlanViewing
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                      data-tutorial="viewing-button"
                    >
                      <Eye className="w-5 h-5" />
                      <span>Plan bezichtiging</span>
                    </button>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showViewingModal && (
        <ViewingRequestModal
          isOpen={showViewingModal}
          onClose={() => setShowViewingModal(false)}
          listing={property}
          listingId={property?.id}
          landlordUid={
            (property?.management?.agencyId &&
              ['active', 'requested'].includes(property?.management?.handoverStatus || 'active'))
              ? property?.management?.agencyId
              : property?.verhuurderID ||
                property?.landlordId ||
                property?.landlordID ||
                property?.ownerId ||
                property?.management?.agencyId ||
                property?.createdByAgencyId ||
                null
          }
        />
      )}
    </div>
  );
}

